import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, deleteDoc, doc, getDocs, updateDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../configs/firebaseconfig';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons'
import '../assets/css/manageprojects.css';

const ManageProjects = () => {
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();
  const [checkedProjects, setCheckedProjects] = useState({});
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/login');
        console.log('User is not authenticated');
      } else {
        console.log('User is authenticated', user.uid);
        const fetchProjects = async () => {
          const projectsQuery = query(collection(db, 'Projects'), orderBy('order'));
          const querySnapshot = await getDocs(projectsQuery);
          const projectsList = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
            showHome: doc.data().showHome || false,
          }));
          console.log('Fetched projects:', projectsList);
          setProjects(projectsList);
          const checkedProjectsInit = projectsList.reduce((acc, project) => {
            acc[project.id] = project.showHome;
            return acc;
          }, {});
          setCheckedProjects(checkedProjectsInit);
        };

        fetchProjects();
      }
    });

    return () => unsubscribe();
  }, [auth, navigate]);

  const handleCheckboxChange = async (projectId, isChecked) => {
    setCheckedProjects((prevState) => ({
      ...prevState,
      [projectId]: isChecked,
    }));

    const projectRef = doc(db, 'Projects', projectId);
    await updateDoc(projectRef, {
      showHome: isChecked,
    });
  };

  const handleEdit = (projectId) => {
    navigate(`/edit-project/${projectId}`);
  };

  const handleDelete = async (projectId) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this project?');
    if (confirmDelete) {
      try {
        await deleteDoc(doc(db, 'Projects', projectId));
        setProjects(projects.filter((project) => project.id !== projectId));
      } catch (error) {
        console.error('Error deleting project:', error);
      }
    }
  };

  const handleAddProject = () => {
    navigate('/AddProjects');
  };

  const handleMoveDown = async (index) => {
    if (index === projects.length - 1) return; // Do nothing if it's the last project

    const updatedProjects = [...projects];
    // Swap the positions of the selected project and the next one
    [updatedProjects[index], updatedProjects[index + 1]] = [
      updatedProjects[index + 1],
      updatedProjects[index],
    ];

    setProjects(updatedProjects);

    // Update the order in Firestore
    const projectRef1 = doc(db, 'Projects', updatedProjects[index].id);
    const projectRef2 = doc(db, 'Projects', updatedProjects[index + 1].id);

    await updateDoc(projectRef1, { order: index + 1 });
    await updateDoc(projectRef2, { order: index + 2 });
  };
  const handleMoveUp = async (index) => {
    if (index === 0) return; // Do nothing if it's the first project
  
    const updatedProjects = [...projects];
    // Swap the positions of the selected project and the previous one
    [updatedProjects[index], updatedProjects[index - 1]] = [
      updatedProjects[index - 1],
      updatedProjects[index],
    ];
  
    setProjects(updatedProjects);
  
    // Update the order in Firestore
    const projectRef1 = doc(db, 'Projects', updatedProjects[index].id);
    const projectRef2 = doc(db, 'Projects', updatedProjects[index - 1].id);
  
    await updateDoc(projectRef1, { order: index + 1 });
    await updateDoc(projectRef2, { order: index });
  };
  
  const memoizedProjects = useMemo(() => projects, [projects]);

  return (
    <div className="relative overflow-x-auto justify-between manage-projects-container">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold">Manage Projects</h1>
        <button
          onClick={handleAddProject}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Add Project
        </button>
      </div>
      <table className="w-full border-collapse border border-gray-200 text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
        <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
          <tr>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700 justify-center">
              Select
            </th>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700 justify-center">
              Project name
            </th>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700 justify-center">
              Category
            </th>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700">
              Image
            </th>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700">
              Actions
            </th>
            <th scope="col" className="px-6 py-3 border border-gray-200 dark:border-black-700">
              Order
            </th>
          </tr>
        </thead>
        <tbody>
          {memoizedProjects.map((project, index) => (
            <tr key={project.id} className="border-b border-gray-200 dark:border-gray-700">
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700 justify-center">
                <input
                  type="checkbox"
                  checked={checkedProjects[project.id] || false}
                  value={project.id}
                  onChange={(e) => handleCheckboxChange(project.id, e.target.checked)}
                  className="form-checkbox h-5 w-5 align-middle content-center border-gray-300 rounded dark:bg-gray-800 dark:border-gray-600 justify-center"
                />
              </td>
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700">{project.name}</td>
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700">{project.category}</td>
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700">
                {project.images && project.images.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {project.images.map((image, idx) => (
                      <img
                        key={idx}
                        src={image}
                        alt={`${project.name} ${idx + 1}`}
                        width="100"
                        className="rounded-md shadow-sm"
                      />
                    ))}
                  </div>
                ) : (
                  <p>No images available</p>
                )}
              </td>
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleEdit(project.id)}
                  className="text-blue-500 hover:underline pr-1"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(project.id)}
                  className="text-blue-500 hover:underline pr-1"
                >
                  Delete
                </button>
               
              </td>
              <td className="px-6 py-4 border border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleMoveUp(index)}
                  className="text-black hover:underline p-2 m-1 bg-green-500 rounded-md"
                >
                     <FontAwesomeIcon icon={faChevronUp} />

                </button>
                <button
                  onClick={() => handleMoveDown(index)}
                  className="text-black hover:underline p-2 m-1 bg-green-500 rounded-md"
                >
                      <FontAwesomeIcon  icon={faChevronDown} />

                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ManageProjects;
